/* eslint-disable no-bitwise */
export class BitReader {

  // Current byte
  private pos = 0;

  private remainingBitsInCurrentByte = 8;

  constructor(private buffer: Uint8Array) {}

  setNewData(newBuffer: Uint8Array) {
    if (this.hasMoreData()) {
      throw new Error('Cannot set new data since current data has not been fully consumed');
    }

    this.buffer = newBuffer;
    this.pos = 0;
    this.remainingBitsInCurrentByte = 8;
  }


  readBits(nBits: number): number {
    if (this.pos >= this.buffer.length) {
      throw new Error('Buffer already consumed entirely');
    }

    const availableBits = this.remainingBitsInCurrentByte + 8 * (this.buffer.length - this.pos - 1);
    if (nBits > availableBits) {
      throw new Error(`${availableBits} bits available, cannot read ${nBits}`);
    }

    const nBitsToReadFromCurrentByte = Math.min(this.remainingBitsInCurrentByte, nBits);

    // First, let's read some bits from the first byte
    const byte = this.buffer[this.pos];
    const mask = (1 << this.remainingBitsInCurrentByte) - 1;

    this.remainingBitsInCurrentByte -= nBitsToReadFromCurrentByte;
    nBits -= nBitsToReadFromCurrentByte;

    let value = (byte & mask) >> this.remainingBitsInCurrentByte;

    if (this.remainingBitsInCurrentByte === 0) {
      this.remainingBitsInCurrentByte = 8;
      this.pos++;
    }

    // Then, let's read some full bytes
    while (nBits >= 8) {
      value = (value << 8) | this.buffer[this.pos++];
      nBits -= 8;
    }

    if (nBits === 0) {
      return value;
    }

    // Then, let's read the last bits, if needed
    this.remainingBitsInCurrentByte = 8 - nBits;
    const lastBits = this.buffer[this.pos] >> this.remainingBitsInCurrentByte;

    value = (value << nBits) | lastBits;
    return value;
  }


  hasMoreData(): boolean {
    return (this.pos <= this.buffer.length - 1) && this.remainingBitsInCurrentByte > 0;
  }


  alignByte() {
    if (this.remainingBitsInCurrentByte !== 8) {
      this.readBits(this.remainingBitsInCurrentByte);
    }
  }


  readUint16LE() {
    const b = this.readBits(8);
    const a = this.readBits(8);
    return a * 256 + b;
  }


  readInt16LE() {
    const n = this.readUint16LE();
    return (n > 0x7FFF) ? n - 0x10000 : n;
  }


  /**
   * An unsigned integer n is encoded with exponential-Golomb encoding
   * by adding 1 to n (to avoid zero), counting the number z of bits it
   * takes to write n+1, writing z-1 zeroes followed by n+1.
   *
   * Example: 7 => 0001000
   */
  readUnsignedExpGolomb() {
    let z = 0;
    while (this.readBits(1) === 0) {
      z++;
    }

    if (z === 0) {
      // If the first bit was a 1, then n+1 is 1 so n is 0
      return 0;
    }

    const z2 = z % 8;
    z = z - z2;
    let nPlusOne = (1 << z2) | this.readBits(z2);

    while (z > 0) {
      nPlusOne = nPlusOne * 256 + this.readBits(8);
      z = z - 8;
    }
    return nPlusOne - 1;
  }


  /**
   * Signed integers are encoded using the following trick to map them
   * to unsigned integers:
   * - x <= 0 becomes -2x
   * - x > 0  becomes 2x-1
   */
  readSignedExpGolomb() {
    const tmp = this.readUnsignedExpGolomb();
    if (tmp === 0) {
      // If we merged this with the regular case for even numbers,
      // we would get -0 which is not exactly the same as 0
      return 0;
    }

    if (tmp % 2 === 0) {
      return (tmp / -2);
    }
    return (tmp + 1) / 2;
  }

}
